import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn, Banknote, History, Plus, Trash2, ImagePlus, Loader2, AlertTriangle, X, Copy, Send, KeyRound, Eye, EyeOff, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fmtD, fmtDT } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, Card } from "@/components/kit";
import { mergeContent, imageUrl } from "@/lib/siteContent";
import { restartDemoTour, useStartOwnAccount } from "@/components/DemoTour";

export function ImageField({ label, value, fallback, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/platform/site-images", fd);
      onChange(data.url);
      toast.success("Image uploaded — publish to make it live");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Field label={label}>
      <img data-testid={`${testid}-preview`} src={imageUrl(value, fallback)} alt="" className="h-40 w-full rounded-xl border border-slate-200 object-cover" />
      <div className="mt-2 flex flex-wrap gap-2">
        <label data-testid={`${testid}-upload`} className="ka-press inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold hover:border-slate-900">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}{busy ? "Uploading…" : "Upload image"}
          <input data-testid={`${testid}-input`} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={busy} onChange={pick} />
        </label>
        {value && <button type="button" data-testid={`${testid}-reset`} onClick={() => onChange("")} className="ka-press rounded-full px-3 py-2 text-xs font-semibold text-slate-500 hover:text-rose-600">Use default image</button>}
      </div>
    </Field>
  );
}

const METHODS = ["bank_transfer", "cash", "cheque", "card_offline", "complimentary", "other"];

const NEW_BIZ = { business_name: "", venue_name: "", owner_name: "", owner_email: "", owner_password: "", package_id: "", trial_days: 7, send_welcome: true, mode: "invite" };

export function InviteLink({ link, email, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">Invite sent</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">We've emailed <b>{email}</b> a link to choose their password. It works once, for 7 days. If email isn't set up yet, copy the link and send it yourself.</p>
        <div data-testid="invite-link-text" className="break-all rounded-lg bg-slate-100 p-3 font-mono text-xs">{link}</div>
        <button data-testid="copy-invite-link-button" onClick={() => { navigator.clipboard.writeText(link); toast.success("Invite link copied"); }} className="ka-press inline-flex items-center justify-center gap-1 rounded-full bg-emerald-600 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"><Copy className="h-4 w-4" />Copy link</button>
      </DialogContent>
    </Dialog>
  );
}

export function CreateBusinessButton({ onCreated }) {
  const [f, setF] = useState(null);
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState(null);
  const open = () => {
    setF(NEW_BIZ);
    api.get("/public/branding").then((r) => setF((cur) => cur && { ...cur, trial_days: r.data.trial_days })).catch(() => {});
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { mode, ...rest } = f;
      const body = { ...rest, trial_days: Number(f.trial_days), package_id: f.package_id || null, owner_password: mode === "password" ? f.owner_password : null };
      const { data } = await api.post("/platform/businesses", body);
      toast.success(`${data.name} created`);
      if (data.invite_link) setInvite({ link: data.invite_link, email: f.owner_email });
      setF(null);
      onCreated();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button data-testid="create-business-button" onClick={open} className="ka-press inline-flex items-center gap-1 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"><Plus className="h-4 w-4" />New business</button>
      <Dialog open={!!f} onOpenChange={(o) => !o && setF(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">Create a business</DialogTitle></DialogHeader>
          {f && (
            <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
              <Field label="Business name *"><input data-testid="new-business-name-input" required minLength={2} className="ka-input" value={f.business_name} onChange={set("business_name")} /></Field>
              <Field label="First venue *"><input data-testid="new-business-venue-input" required minLength={2} className="ka-input" value={f.venue_name} onChange={set("venue_name")} /></Field>
              <Field label="Owner name *"><input data-testid="new-business-owner-name-input" required className="ka-input" value={f.owner_name} onChange={set("owner_name")} /></Field>
              <Field label="Owner email *"><input data-testid="new-business-owner-email-input" type="email" required className="ka-input" value={f.owner_email} onChange={set("owner_email")} /></Field>
              <Field label="Owner's password" className="sm:col-span-2">
                <div className="flex flex-wrap gap-2">{[["invite", "Email an invite link"], ["password", "Set a password now"]].map(([k, l]) => <button type="button" key={k} data-testid={`new-business-mode-${k}`} onClick={() => setF({ ...f, mode: k })} className={`ka-press rounded-full px-4 py-1.5 text-sm font-semibold ${f.mode === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>)}</div>
              </Field>
              {f.mode === "password" ? <>
                <Field label="Password *" hint="At least 8 characters. Share it securely." className="sm:col-span-2"><input data-testid="new-business-owner-password-input" type="password" autoComplete="new-password" minLength={8} required className="ka-input" value={f.owner_password} onChange={set("owner_password")} /></Field>
              </> : <p className="text-xs text-slate-500 sm:col-span-2">The owner gets an email with a link to choose their own password (valid 7 days).</p>}
              <Field label="Package"><select data-testid="new-business-package-select" className="ka-input" value={f.package_id} onChange={set("package_id")}><option value="">Default</option>{packages.map((p) => <option key={p.id} value={p.id}>{p.name} · £{p.price_per_venue.toFixed(2)}</option>)}</select></Field>
              <Field label="Free trial (days)"><input data-testid="new-business-trial-input" type="number" min={0} max={365} className="ka-input" value={f.trial_days} onChange={set("trial_days")} /></Field>
              {f.mode === "password" && <label className="flex items-center gap-2 text-sm sm:col-span-2"><input data-testid="new-business-welcome-checkbox" type="checkbox" checked={f.send_welcome} onChange={set("send_welcome")} />Send the owner a welcome email</label>}
              <button data-testid="save-new-business-button" disabled={busy} className="ka-press rounded-full bg-emerald-600 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60 sm:col-span-2">{busy ? "Creating…" : "Create business"}</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
      {invite && <InviteLink {...invite} onClose={() => setInvite(null)} />}
    </>
  );
}

function PaymentDialog({ b, onClose, onSaved }) {
  const [f, setF] = useState({ venues: Math.max(1, b.venues), months: 1, amount: 69 * Math.max(1, b.venues), method: "bank_transfer", reference: "", note: "" });
  const [history, setHistory] = useState(null);
  useEffect(() => {
    api.get(`/platform/businesses/${b.id}/payments`).then((r) => setHistory(r.data));
  }, [b.id]);
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/platform/businesses/${b.id}/manual-payment`, { ...f, venues: Number(f.venues), months: Number(f.months), amount: Number(f.amount) });
      toast.success("Payment recorded — subscription active");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">Record payment · {b.name}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
          <Field label="Venues covered"><input data-testid="manual-venues-input" type="number" min={1} required className="ka-input" value={f.venues} onChange={(e) => set("venues", e.target.value)} /></Field>
          <Field label="Months paid"><input data-testid="manual-months-input" type="number" min={1} max={36} required className="ka-input" value={f.months} onChange={(e) => set("months", e.target.value)} /></Field>
          <Field label="Amount received (£)"><input data-testid="manual-amount-input" type="number" step="0.01" min={0} required className="ka-input" value={f.amount} onChange={(e) => set("amount", e.target.value)} /></Field>
          <Field label="Method"><select data-testid="manual-method-select" className="ka-input" value={f.method} onChange={(e) => set("method", e.target.value)}>{METHODS.map((m) => <option key={m} value={m}>{m.replace("_", " ")}</option>)}</select></Field>
          <Field label="Reference" className="sm:col-span-2"><input data-testid="manual-reference-input" className="ka-input" value={f.reference} onChange={(e) => set("reference", e.target.value)} placeholder="e.g. bank transfer ref" /></Field>
          <Field label="Note" className="sm:col-span-2"><textarea data-testid="manual-note-input" rows={2} className="ka-input" value={f.note} onChange={(e) => set("note", e.target.value)} /></Field>
          <button data-testid="save-manual-payment-button" className="ka-press rounded-full bg-emerald-600 py-3 text-sm font-semibold text-white hover:bg-emerald-700 sm:col-span-2">Record payment & activate</button>
        </form>
        <div className="mt-4 border-t border-slate-200 pt-4">
          <div className="ka-eyebrow mb-2 flex items-center gap-1"><History className="h-3 w-3" />Payment history</div>
          <ul data-testid="payment-history" className="space-y-2 text-sm">
            {(history?.manual || []).map((p) => <li key={p.id} className="flex justify-between gap-2"><span>£{p.amount.toFixed(2)} · {p.method.replace("_", " ")} · {p.venues} venue(s) × {p.months}m {p.reference && `· ${p.reference}`}</span><span className="text-xs text-slate-500">until {fmtD(p.paid_until)}</span></li>)}
            {(history?.stripe || []).map((p) => <li key={p.session_id} className="flex justify-between gap-2"><span>£{Number(p.amount).toFixed(2)} · Stripe · {p.quantity} venue(s)</span><span className="text-xs text-slate-500">{fmtDT(p.created_at)}</span></li>)}
            {history && !history.manual.length && !history.stripe.length && <li className="text-slate-500">No payments yet.</li>}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PasswordResetDialog({ endpoint, title, onClose }) {
  const [mode, setMode] = useState("email");
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, mode === "set" ? { mode, password: pw } : { mode });
      setDone({ ...data, mode });
      toast.success(mode === "set" ? "New password saved" : `Reset link sent to ${data.email}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const tab = (k, l) => <button type="button" data-testid={`reset-mode-${k}`} onClick={() => setMode(k)} className={`ka-press flex-1 rounded-full px-3 py-2 text-sm font-semibold ${mode === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="owner-password-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        {done ? (
          <div className="space-y-3 text-sm">
            <p data-testid="owner-password-done">{done.mode === "set" ? <>The new password is saved for <b>{done.email}</b>. They've been signed out everywhere; tell them the new password yourself.</> : <>We've emailed a reset link to <b>{done.email}</b>. It works once, for 24 hours. If the email doesn't arrive, send them this link:</>}</p>
            {done.reset_link && <div className="flex gap-2"><div data-testid="owner-reset-link" className="flex-1 break-all rounded-lg bg-slate-100 p-3 font-mono text-xs">{done.reset_link}</div><button data-testid="owner-reset-link-copy" onClick={() => { navigator.clipboard.writeText(done.reset_link); toast.success("Link copied"); }} className="ka-press rounded-full bg-emerald-600 px-3 text-white"><Copy className="h-4 w-4" /></button></div>}
            <button data-testid="owner-password-close" onClick={onClose} className="ka-press w-full rounded-full border border-slate-300 py-2.5 font-semibold">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="flex gap-2">{tab("email", "Email a reset link")}{tab("set", "Type a new password")}</div>
            {mode === "set" ? <Field label="New password" hint="At least 8 characters. They're signed out everywhere."><div className="relative"><input data-testid="owner-new-password-input" type={showPw ? "text" : "password"} autoComplete="new-password" minLength={8} maxLength={128} required className="ka-input pr-11" value={pw} onChange={(e) => setPw(e.target.value)} /><button type="button" data-testid="owner-new-password-toggle" aria-label={showPw ? "Hide password" : "Show password"} onClick={() => setShowPw(!showPw)} className="ka-press absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-slate-400 hover:text-slate-800">{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button></div></Field>
              : <p className="text-sm text-slate-600">They get an email with a link to choose their own password. The link works once, for 24 hours.</p>}
            <button data-testid="owner-password-submit" disabled={busy} className="ka-press flex w-full items-center justify-center gap-2 rounded-full bg-slate-950 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{mode === "set" ? "Save new password" : "Send reset link"}</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ExtendTrialDialog({ b, onClose, onSaved }) {
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const now = new Date();
  const cur = b.trial_ends_at ? new Date(b.trial_ends_at) : null;
  const base = cur && cur > now ? cur : now;
  const next = new Date(base.getTime() + Number(days || 0) * 86400000);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/platform/businesses/${b.id}/extend-trial`, { days: Number(days) });
      toast.success(`Trial extended to ${fmtD(next.toISOString())}`);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle className="font-display text-2xl">Extend trial · {b.name}</DialogTitle></DialogHeader>
        <p data-testid="extend-trial-current" className="text-sm text-slate-600">{cur ? `Trial ${cur > now ? "ends" : "ended"} ${fmtD(b.trial_ends_at)}.` : "No trial date set."} Days are added {cur && cur > now ? "on top of the current end date" : "from today"}.</p>
        <form onSubmit={save} className="grid gap-4">
          <div className="flex gap-2">{[7, 14, 30].map((d) => <button type="button" key={d} data-testid={`extend-trial-quick-${d}`} onClick={() => setDays(d)} className={`ka-press rounded-full px-4 py-1.5 text-xs font-semibold ${Number(days) === d ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>+{d} days</button>)}</div>
          <Field label="Extra days"><input data-testid="extend-trial-days-input" type="number" min={1} max={365} required className="ka-input" value={days} onChange={(e) => setDays(e.target.value)} /></Field>
          <p data-testid="extend-trial-new-date" className="text-sm font-semibold text-slate-900">New end date: {fmtD(next.toISOString())}</p>
          <button data-testid="extend-trial-save" disabled={busy} className="ka-press inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-600 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Extend trial</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function BusinessActions({ b, onChanged }) {
  const { applySession, user } = useAuth();
  const nav = useNavigate();
  const [paying, setPaying] = useState(false);
  const [invite, setInvite] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [extending, setExtending] = useState(false);
  const resend = async () => {
    try {
      const { data } = await api.post(`/platform/businesses/${b.id}/resend-invite`);
      setInvite({ link: data.invite_link, email: data.email });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const loginAs = async () => {
    try {
      const { data } = await api.post(`/platform/businesses/${b.id}/impersonate`);
      await applySession(data);
      toast.success(`Signed in as ${b.name}`);
      nav("/app");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const setStatus = async (s) => {
    try {
      await api.post(`/platform/businesses/${b.id}/status`, { subscription_status: s });
      toast.success("Status updated");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5">
      <select data-testid={`status-select-${b.id}`} className="ka-input w-32 py-1 text-xs" value={b.subscription_status} onChange={(e) => setStatus(e.target.value)}>
        {["trialing", "active", "past_due", "cancelled"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
      </select>
      <button data-testid={`record-payment-${b.id}`} onClick={() => setPaying(true)} className="ka-press inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:border-slate-900"><Banknote className="h-3.5 w-3.5" />Payment</button>
      {b.id !== user.business_id && <button data-testid={`login-as-${b.id}`} onClick={loginAs} className="ka-press inline-flex items-center gap-1 rounded-full bg-slate-950 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"><LogIn className="h-3.5 w-3.5" />Sign in as</button>}
      {b.owner_invite_pending && !b.is_demo && <button data-testid={`resend-invite-${b.id}`} onClick={resend} className="ka-press inline-flex items-center gap-1 rounded-full border border-amber-400 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-900 hover:border-amber-600"><Send className="h-3.5 w-3.5" />Resend invite</button>}
      {invite && <InviteLink {...invite} onClose={() => setInvite(null)} />}
      {!b.is_demo && !b.owner_invite_pending && <button data-testid={`reset-owner-password-${b.id}`} onClick={() => setResetting(true)} className="ka-press inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:border-slate-900"><KeyRound className="h-3.5 w-3.5" />Reset password</button>}
      {resetting && <PasswordResetDialog endpoint={`/platform/businesses/${b.id}/reset-owner-password`} title={`Reset owner password · ${b.name}`} onClose={() => setResetting(false)} />}
      {!b.is_demo && b.subscription_status !== "active" && <button data-testid={`extend-trial-${b.id}`} onClick={() => setExtending(true)} className="ka-press inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:border-slate-900"><CalendarPlus className="h-3.5 w-3.5" />Extend trial</button>}
      {extending && <ExtendTrialDialog b={b} onClose={() => setExtending(false)} onSaved={() => { setExtending(false); onChanged(); }} />}
      {paying && <PaymentDialog b={b} onClose={() => setPaying(false)} onSaved={() => { setPaying(false); onChanged(); }} />}
    </div>
  );
}

function ListEditor({ label, items, onChange, testid }) {
  return (
    <Field label={label} className="sm:col-span-2">
      <div className="space-y-2">
        {items.map((t, i) => (
          <div key={i} className="flex gap-2">
            <input data-testid={`${testid}-${i}`} className="ka-input" value={t} onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} />
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="ka-press rounded-lg px-2 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <button type="button" data-testid={`${testid}-add`} onClick={() => onChange([...items, ""])} className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:underline"><Plus className="h-3.5 w-3.5" />Add</button>
      </div>
    </Field>
  );
}

const TEXT_FIELDS = [
  ["announcement", "Announcement bar (leave blank to hide)"], ["hero_eyebrow", "Hero eyebrow"], ["hero_title_1", "Hero headline line 1"],
  ["hero_title_2", "Hero headline line 2 (green)"], ["hero_subtitle", "Hero subtitle", true], ["hero_cta", "Hero button text"],
  ["features_eyebrow", "Features eyebrow"], ["features_title", "Features heading"], ["features_text", "Features intro", true],
  ["staff_eyebrow", "Staff app eyebrow"], ["staff_title", "Staff app heading"], ["staff_text", "Staff app text", true],
  ["pricing_title", "Pricing heading"], ["pricing_text", "Pricing text", true], ["price_amount", "Price shown"],
  ["price_unit", "Price unit"], ["price_note", "Price note"], ["footer_text", "Footer text"],
];

export function CmsEditor() {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    api.get("/public/content").then((r) => live && setC((cur) => cur || mergeContent(r.data)));
    return () => { live = false; };
  }, []);
  if (!c) return null;
  const set = (k, v) => setC({ ...c, [k]: v });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/platform/content", { ...c, features: c.features.map(({ title, text }) => ({ title, text })) });
      setC(mergeContent(data));
      toast.success("Website updated");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card>
      <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
        <p className="text-sm text-slate-500 sm:col-span-2">Edit the public landing page. The price shown here is display text only — the amount charged at checkout comes from the Packages tab.</p>
        <ImageField label="Hero image (landscape works best)" testid="cms-hero-image" value={c.hero_image} fallback="/img/hero.jpg" onChange={(v) => set("hero_image", v)} />
        <ImageField label="Staff app section image" testid="cms-staff-image" value={c.staff_image} fallback="/img/staff.jpg" onChange={(v) => set("staff_image", v)} />
        {TEXT_FIELDS.map(([k, label, long]) => (
          <Field key={k} label={label} className={long ? "sm:col-span-2" : ""}>
            {long ? <textarea data-testid={`cms-${k}`} rows={3} className="ka-input" value={c[k]} onChange={(e) => set(k, e.target.value)} />
              : <input data-testid={`cms-${k}`} className="ka-input" value={c[k]} onChange={(e) => set(k, e.target.value)} />}
          </Field>
        ))}
        <ListEditor label="Hero bullet points (max 4)" testid="cms-hero-point" items={c.hero_points} onChange={(v) => set("hero_points", v.slice(0, 4))} />
        <div className="space-y-3 sm:col-span-2">
          <div className="text-sm font-medium text-slate-700">Feature blocks (6)</div>
          {c.features.map((f, i) => (
            <div key={i} className="grid gap-2 rounded-xl border border-slate-200 p-3 sm:grid-cols-[1fr_2fr]">
              <input data-testid={`cms-feature-title-${i}`} className="ka-input" value={f.title} onChange={(e) => set("features", c.features.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
              <input data-testid={`cms-feature-text-${i}`} className="ka-input" value={f.text} onChange={(e) => set("features", c.features.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
            </div>
          ))}
        </div>
        <ListEditor label="Pricing checklist" testid="cms-price-feature" items={c.price_features} onChange={(v) => set("price_features", v.slice(0, 12))} />
        <div className="flex gap-2 sm:col-span-2">
          <button data-testid="cms-save-button" disabled={busy} className="ka-press rounded-full bg-emerald-600 px-6 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">{busy ? "Saving…" : "Publish changes"}</button>
          <a href="/" target="_blank" rel="noreferrer" data-testid="cms-preview-link" className="ka-press rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold hover:border-slate-900">View site</a>
        </div>
      </form>
    </Card>
  );
}

export function DemoBanner() {
  const { business } = useAuth();
  const startOwn = useStartOwnAccount();
  if (!business?.is_demo) return null;
  return (
    <div data-testid="demo-banner" className="no-print flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-sky-200 bg-sky-50 px-5 py-2.5 text-sm text-sky-950 lg:pl-[17rem]">
      <b>You're exploring the demo kitchen.</b>
      <span>It's shared with other visitors and resets every night at 3am with fresh data for today.</span>
      <button data-testid="demo-tour-restart" onClick={restartDemoTour} className="font-semibold text-sky-800 hover:underline">Take the tour</button>
      <button data-testid="demo-signup-link" onClick={() => startOwn()} className="font-semibold text-emerald-700 hover:underline">Create your own free account →</button>
    </div>
  );
}

export function PaymentReminder() {
  const { business, can } = useAuth();
  const [hidden, setHidden] = useState(() => sessionStorage.getItem("ka_pay_reminder") === business?.paid_until);
  if (!business || business.payment_method !== "manual" || !business.paid_until || hidden || !can("admin")) return null;
  const days = Math.ceil((new Date(business.paid_until) - Date.now()) / 864e5);
  const expired = days <= 0 || business.subscription_status === "past_due";
  if (!expired && days > 7) return null;
  const dismiss = () => {
    sessionStorage.setItem("ka_pay_reminder", business.paid_until);
    setHidden(true);
  };
  return (
    <div data-testid="payment-reminder-banner" className={`no-print flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm font-medium lg:ml-64 ${expired ? "bg-rose-600 text-white" : "bg-amber-100 text-amber-950"}`}>
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span data-testid="payment-reminder-text">
        {expired ? (days > 0 ? <>Your payment is overdue. Please arrange payment to keep your records active.</> : <>Your plan ended on <b>{fmtD(business.paid_until)}</b>. Please arrange payment to keep your records active.</>)
          : <>Your plan runs out in <b>{days} day{days === 1 ? "" : "s"}</b> ({fmtD(business.paid_until)}). Please arrange your next payment.</>}
      </span>
      <button data-testid="payment-reminder-dismiss" onClick={dismiss} className="ml-auto rounded-full p-1 hover:bg-black/10" aria-label="Dismiss"><X className="h-4 w-4" /></button>
    </div>
  );
}

export function ImpersonationBanner() {
  const { user, business, applySession } = useAuth();
  const nav = useNavigate();
  if (!user?.impersonated_by) return null;
  const back = async () => {
    try {
      const { data } = await api.post("/platform/stop-impersonation");
      await applySession(data);
      nav("/app/platform");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid="impersonation-banner" className="no-print sticky top-0 z-[45] flex flex-wrap items-center gap-3 bg-amber-400 px-4 py-2 text-sm font-medium text-amber-950 lg:ml-64">
      <span>Support view: signed in as <b>{user.name}</b> at <b>{business?.name}</b>. Everything you do is logged.</span>
      <button data-testid="stop-impersonation-button" onClick={back} className="ka-press ml-auto rounded-full bg-amber-950 px-4 py-1 text-xs font-semibold text-white">Return to platform admin</button>
    </div>
  );
}
